import { useEffect, useRef, useState } from 'react'

export interface CashActivity { dirty: boolean; busy: boolean; uncertain: boolean }
export const cleanActivity: CashActivity = { dirty: false, busy: false, uncertain: false }
export const cashUpdateBlocked = (activity: CashActivity, authenticating: boolean, formOpen: boolean) => authenticating || activity.dirty || activity.busy || activity.uncertain || formOpen
interface InstallEvent extends Event { prompt(): Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }
type InstallResult = 'accepted' | 'dismissed' | 'guide'
const inApp = () => window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true

export function useCashPwa(blocked: boolean, durableBlocked: () => boolean = () => false) {
  const [installed, setInstalled] = useState(inApp), [canInstall, setCanInstall] = useState(false), [installing, setInstalling] = useState(false)
  const [waiting, setWaiting] = useState<ServiceWorker | null>(null), [updating, setUpdating] = useState(false), [checking, setChecking] = useState(false), [updateError, setUpdateError] = useState(false)
  const registration = useRef<ServiceWorkerRegistration | null>(null), installEvent = useRef<InstallEvent | null>(null), blockRef = useRef(blocked)
  blockRef.current = blocked
  const durableRef = useRef(durableBlocked), updateLock = useRef(false), updateCleanup = useRef<(() => void) | null>(null)
  durableRef.current = durableBlocked
  useEffect(() => () => updateCleanup.current?.(), [])
  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)')
    const change = () => setInstalled(inApp())
    const prompt = (event: Event) => { event.preventDefault(); installEvent.current = event as InstallEvent; setCanInstall(true) }
    const added = () => { installEvent.current = null; setCanInstall(false); setInstalled(true) }
    window.addEventListener('beforeinstallprompt', prompt); window.addEventListener('appinstalled', added); media.addEventListener?.('change', change)
    return () => { window.removeEventListener('beforeinstallprompt', prompt); window.removeEventListener('appinstalled', added); media.removeEventListener?.('change', change) }
  }, [])
  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return
    let alive = true; const watched = new Set<ServiceWorker>()
    const reconcile = () => {
      if (!alive) return
      const candidate = registration.current?.waiting
      setWaiting(registration.current?.active && candidate?.state === 'installed' ? candidate : null)
      for (const worker of [registration.current?.installing, candidate]) if (worker && !watched.has(worker)) {
        watched.add(worker); worker.addEventListener('statechange', reconcile)
      }
    }
    const check = () => { if (navigator.onLine && document.visibilityState !== 'hidden') void registration.current?.update().catch(() => undefined) }
    void navigator.serviceWorker.register('/sw.js', { scope: '/' }).then(value => {
      if (!alive) return
      registration.current = value; value.addEventListener('updatefound', reconcile); reconcile()
    }).catch(() => undefined)
    const timer = setInterval(check, 15 * 60 * 1000)
    window.addEventListener('online', check)
    return () => { alive = false; clearInterval(timer); window.removeEventListener('online', check); registration.current?.removeEventListener('updatefound', reconcile); for (const worker of watched) worker.removeEventListener('statechange', reconcile) }
  }, [])
  const install = async (): Promise<InstallResult> => {
    const event = installEvent.current
    if (!event) return 'guide'
    setInstalling(true)
    try { await event.prompt(); const choice = await event.userChoice; installEvent.current = null; setCanInstall(false); return choice.outcome }
    finally { setInstalling(false) }
  }
  const checkUpdate = async () => {
    setChecking(true)
    try {
      if (!registration.current || !navigator.onLine) throw new Error('OFFLINE')
      await registration.current.update()
      const installing = registration.current.installing
      if (installing && ['installing', 'installed'].includes(installing.state)) {
        await new Promise<void>(resolve => {
          const complete = () => { clearTimeout(timer); installing.removeEventListener('statechange', changed); resolve() }
          const changed = () => { if (installing.state !== 'installing') complete() }
          const timer = setTimeout(complete, 10000)
          installing.addEventListener('statechange', changed); changed()
        })
        await new Promise(resolve => setTimeout(resolve, 0))
      }
      const candidate = registration.current.waiting
      setWaiting(candidate?.state === 'installed' ? candidate : null)
      return Boolean(candidate)
    } finally { setChecking(false) }
  }
  const applyUpdate = async () => {
    if (updateLock.current || blockRef.current || durableRef.current() || document.querySelector('dialog.cash-dialog[open]') || !waiting || !navigator.onLine) return false
    const candidate = registration.current?.waiting
    if (candidate !== waiting || candidate.state !== 'installed') return false
    updateLock.current = true; setUpdateError(false); setUpdating(true)
    // No automatic controllerchange reload. Only this explicit safe action arms
    // reload; other tabs keep their forms and original recovery references.
    let finished = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const cleanup = () => { if (timer) clearTimeout(timer); navigator.serviceWorker.removeEventListener('controllerchange', reload) }
    const failed = () => { finished = true; cleanup(); updateLock.current = false; setUpdating(false); setUpdateError(true) }
    const reload = () => {
      if (finished) return
      // A receipt created by another tab after the click still wins over reload.
      // The updating screen prevents this tab from starting any new input.
      if (blockRef.current || durableRef.current() || document.querySelector('dialog.cash-dialog[open]')) { failed(); return }
      finished = true; cleanup(); window.location.reload()
    }
    updateCleanup.current = cleanup
    navigator.serviceWorker.addEventListener('controllerchange', reload)
    try {
      candidate.postMessage({ type: 'CASH_APPLY_UPDATE' })
      timer = setTimeout(() => { if (candidate.state === 'activated') reload(); else failed() }, 5000)
    }
    catch { failed(); return false }
    return true
  }
  return { installed, canInstall, installing, install, waiting, updating, updateError, checking, checkUpdate, applyUpdate }
}
